// Local UI data only. Never pass an Auth session or provider response to this module.
export const DRAFT_VERSION = 1;
export const DRAFT_CHANGED_EVENT = 'quikpix:local-draft-changed';
export const MAX_DRAFT_BYTES = 2_000_000;
export interface DraftScope { adminId: string; context: string; }
export interface EditorDraft<T> {
  version: 1; key: string; baseline: string; savedAt: string; value: T;
}
const credentialPattern = /(?:"(?:access_token|refresh_token|id_token|authorization|service_role_key)"\s*:|\bBearer\s+[A-Za-z0-9_.-]+|\bsb_secret_[A-Za-z0-9_-]+|\bsk-(?:proj-|svcacct-)?[A-Za-z0-9_-]{20,}|\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+|[?&](?:token|access_token|signature|api_key|apikey)=)/i;
export function stableValue(value: unknown): string {
  return JSON.stringify(value, (_key, entry) => entry && typeof entry === 'object' && !Array.isArray(entry)
    ? Object.keys(entry).sort().reduce((result: any, key) => { result[key] = entry[key]; return result; }, {}) : entry);
}
export function draftKey(scope: DraftScope, quoteId?: string | null): string {
  // A tab-local new-Quote slot remains stable through reload and navigation. Saved/revised Quotes use their UUID.
  return `quikpix:quote-draft:v1:${encodeURIComponent(scope.context)}:${encodeURIComponent(scope.adminId)}:${encodeURIComponent(quoteId || 'new')}`;
}
export function readDraft<T>(key: string, validate: (value: unknown) => value is T): EditorDraft<T> | null {
  const text = sessionStorage.getItem(key);
  if (!text) return null;
  if (text.length > MAX_DRAFT_BYTES || credentialPattern.test(text)) throw new Error('Local draft cannot be stored safely');
  const entry = JSON.parse(text);
  if (entry.version !== DRAFT_VERSION || entry.key !== key || typeof entry.baseline !== 'string' ||
      typeof entry.savedAt !== 'string' || !Number.isFinite(Date.parse(entry.savedAt)) || !validate(entry.value)) {
    throw new Error('Local draft is incompatible');
  }
  return entry;
}
export function writeDraft<T>(key: string, baseline: string, value: T): void {
  const text = JSON.stringify({ version: DRAFT_VERSION, key, baseline, savedAt: new Date().toISOString(), value });
  if (text.length > MAX_DRAFT_BYTES || credentialPattern.test(text)) throw new Error('Local draft cannot be stored safely');
  sessionStorage.setItem(key, text);
  window.dispatchEvent(new CustomEvent(DRAFT_CHANGED_EVENT, { detail: { key } }));
}
export function removeDraft(key: string): void {
  sessionStorage.removeItem(key);
  window.dispatchEvent(new CustomEvent(DRAFT_CHANGED_EVENT, { detail: { key } }));
}
export function requestEditorNavigation(action: () => void): void {
  const event = new CustomEvent('quikpix:editor-navigation', { cancelable: true, detail: { action } });
  if (window.dispatchEvent(event)) action();
}
